import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, AlertTriangle, CreditCard, ClipboardList, CheckCheck, Users, UserCog, CalendarClock, GraduationCap } from "lucide-react";

const ICONS = {
  fee_overdue: { icon: AlertTriangle, tint: "text-red-600 bg-red-100" },
  fee_due: { icon: AlertTriangle, tint: "text-amber-600 bg-amber-100" },
  payment: { icon: CreditCard, tint: "text-accent bg-accent/10" },
  enrollment: { icon: ClipboardList, tint: "text-primary bg-primary/10" },
  batch_assigned: { icon: Users, tint: "text-sky-600 bg-sky-100" },
  trainer_assigned: { icon: UserCog, tint: "text-violet-600 bg-violet-100" },
  batch_soon: { icon: CalendarClock, tint: "text-indigo-600 bg-indigo-100" },
  course_completed: { icon: GraduationCap, tint: "text-emerald-600 bg-emerald-100" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setUnread(data.unread_count); } catch { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/notifications", { params: { limit: 20 } }); setItems(data.items || []); setUnread(data.unread_count); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  const openItem = async (n) => {
    if (!n.is_read) { await api.post(`/notifications/${n.id}/read`); }
    if (n.link) navigate(n.link);
    loadList();
  };

  const markAll = async () => { await api.post("/notifications/read-all"); loadList(); };

  return (
    <DropdownMenu onOpenChange={(o) => o && loadList()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-button" className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-slate-100">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {unread > 0 && (
            <button onClick={markAll} data-testid="mark-all-read" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-2 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">You're all caught up. 🎉</div>
          ) : (
            items.map((n) => {
              const cfg = ICONS[n.type] || ICONS.enrollment;
              const Icon = cfg.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  data-testid={`notification-${n.id}`}
                  className={`flex w-full items-start gap-3 border-b border-border px-3 py-2.5 text-left transition-colors hover:bg-slate-50 ${n.is_read ? "" : "bg-primary/[0.04]"}`}
                >
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${cfg.tint} ${n.is_read ? "opacity-70" : ""}`}><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`truncate text-sm text-secondary ${n.is_read ? "font-medium" : "font-semibold"}`}>{n.title}</span>
                      {!n.is_read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">{n.message}</span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
